/**
=========================================================
* ClubOS - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftAvatar from "components/SoftAvatar";

// ClubOS examples
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// ClubOS context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Auth context
import { useAuth } from "context/AuthContext";

// Images
import team2 from "assets/images/team-2.jpg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [profileMenu, setProfileMenu] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleOpenProfileMenu = (event) => setProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setProfileMenu(null);
  const handleGoToProfile = () => { handleCloseProfileMenu(); navigate("/profile"); };
  const handleLogout = async () => { handleCloseProfileMenu(); await logout(); navigate("/authentication/sign-in", { replace: true }); };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        image={<img src={team2} alt="person" />}
        title={["New message", "from Laur"]}
        date="13 minutes ago"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        image={<img src={logoSpotify} alt="person" />}
        title={["New album", "by Travis Scott"]}
        date="1 day"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        color="secondary"
        image={
          <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
            payment
          </Icon>
        }
        title={["", "Payment successfully completed"]}
        date="2 days"
        onClick={handleCloseMenu}
      />
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* Search hidden — preserved for future use */}
            <SoftBox pr={1} sx={{ display: "none" }}>
              <SoftInput
                placeholder="Type here..."
                icon={{ component: "search", direction: "left" }}
              />
            </SoftBox>
            <SoftBox color={light ? "white" : "inherit"}>
              {isAuthenticated && currentUser ? (
                <>
                  <IconButton sx={{ ...navbarIconButton, gap: 0.75 }} size="small" onClick={handleOpenProfileMenu}>
                    <SoftAvatar
                      alt={currentUser.name}
                      size="xs"
                      sx={{ bgcolor: "info.main", fontSize: "0.7rem", width: 28, height: 28 }}
                    >
                      {currentUser.avatar}
                    </SoftAvatar>
                    <SoftTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                      {currentUser.name}
                    </SoftTypography>
                  </IconButton>
                  <Menu
                    anchorEl={profileMenu}
                    open={Boolean(profileMenu)}
                    onClose={handleCloseProfileMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{ mt: 1 }}
                  >
                    <SoftBox px={2} py={1}>
                      <SoftTypography variant="caption" fontWeight="bold" color="secondary">
                        {currentUser.role}
                      </SoftTypography>
                    </SoftBox>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem onClick={handleGoToProfile} sx={{ gap: 1 }}>
                      <Icon fontSize="small">person</Icon>
                      <SoftTypography variant="button" fontWeight="regular">My Profile</SoftTypography>
                    </MenuItem>
                    <MenuItem onClick={handleLogout} sx={{ gap: 1, color: "error.main" }}>
                      <Icon fontSize="small" color="error">logout</Icon>
                      <SoftTypography variant="button" fontWeight="regular" color="error">Sign Out</SoftTypography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Link to="/authentication/sign-in">
                  <IconButton sx={navbarIconButton} size="small">
                    <Icon sx={({ palette: { dark, white } }) => ({ color: light ? white.main : dark.main })}>
                      account_circle
                    </Icon>
                    <SoftTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                      Sign in
                    </SoftTypography>
                  </IconButton>
                </Link>
              )}
              <IconButton
                color="inherit"
                sx={(theme) => ({
                  ...navbarMobileMenu(theme),
                  position: "fixed",
                  top: 12,
                  right: 16,
                  zIndex: 1400,
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "rgba(255,255,255,0.92)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,1)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                  },
                })}
                onClick={handleMiniSidenav}
              >
                <Icon sx={{ fontSize: "1.8rem !important", color: "#344767" }}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon className={light ? "text-white" : "text-dark"}>notifications</Icon>
              </IconButton>
              {renderMenu()}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Default values moved to destructuring parameters

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
